import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Heart, 
  MessageCircle, 
  Sparkles, 
  CheckCircle,
  XCircle,
  ArrowRight,
  BookOpen
} from 'lucide-react';
import { 
  generatePersonalizedResponse, 
  healthStatusMessages,
  habitTrackingMessages,
  DYNAMIC_TONES,
  RESPONSE_EXAMPLES,
  applyPersonality
} from '@/utils/aiPersonality';

/**
 * AI Personality Guide
 * Interactive documentation showing HealthLoop's AI personality in action
 */

const AIPersonalityGuide = () => {
  const [testMessage, setTestMessage] = useState('');
  const [transformedMessage, setTransformedMessage] = useState('');
  const [selectedTone, setSelectedTone] = useState('neutral');

  const handleTransform = () => {
    const result = applyPersonality(testMessage, selectedTone);
    setTransformedMessage(result);
  };

  const handleGenerateExample = (state) => {
    const example = generatePersonalizedResponse({
      userState: state,
      insight: 'You\'ve been tracking your habits consistently',
      actions: ['Keep up the great work', 'Try adding one new healthy habit'],
      severity: 'low'
    });
    setTransformedMessage(example.full);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3">
              <Heart className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            HealthLoop AI Personality
          </h1>
          <p className="text-gray-600">
            A calm, supportive health companion that helps users understand their body
          </p>
        </div>

        {/* Core Identity */}
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              Our Personality Identity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Core Values</h3>
                <div className="space-y-2">
                  <Badge className="bg-blue-100 text-blue-900">Empathy</Badge>
                  <Badge className="bg-green-100 text-green-900 ml-2">Clarity</Badge>
                  <Badge className="bg-purple-100 text-purple-900 ml-2">Empowerment</Badge>
                  <Badge className="bg-pink-100 text-pink-900 ml-2">Trust</Badge>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">What We Avoid</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    Medical diagnosis
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    Judgment or blame
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    Complex jargon
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    Alarming language
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Structure */}
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-blue-600" />
              Every Response Follows This Structure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { num: 1, title: 'Acknowledge', desc: 'Show empathy and understanding', color: 'purple', example: '"Great work!" or "I understand this is tough"' },
                { num: 2, title: 'Insight', desc: 'Provide simple, non-diagnostic observation', color: 'blue', example: '"It looks like your body may need more rest"' },
                { num: 3, title: 'Actions', desc: 'Suggest 1-3 practical steps', color: 'green', example: '"Try going to bed 30 minutes earlier"' },
                { num: 4, title: 'Encourage', desc: 'Offer supportive closing', color: 'pink', example: '"You\'ve got this!"' }
              ].map((step) => (
                <div key={step.num} className={`p-4 rounded-lg border-2 border-${step.color}-200 bg-${step.color}-50`}>
                  <div className="flex items-start gap-3">
                    <span className={`bg-${step.color}-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0`}>
                      {step.num}
                    </span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                      <p className="text-sm text-gray-700 mb-2">{step.desc}</p>
                      <p className="text-xs text-gray-600 italic">{step.example}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Language Transformations */}
        <Card className="border-2 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRight className="h-6 w-6 text-green-600" />
              Language Transformations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="diagnosis" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
                <TabsTrigger value="negative">Negative</TabsTrigger>
                <TabsTrigger value="alarm">Alarm</TabsTrigger>
                <TabsTrigger value="commands">Commands</TabsTrigger>
              </TabsList>
              
              <TabsContent value="diagnosis" className="space-y-4 mt-4">
                <div className="space-y-3">
                  {[
                    { bad: 'You have dehydration', good: 'It looks like your body may need more water' },
                    { bad: 'You are diagnosed with poor sleep', good: 'Your sleep pattern could use some attention' },
                    { bad: 'This indicates stress', good: 'This could be related to stress levels' }
                  ].map((item, i) => (
                    <div key={i} className="grid md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-xs font-semibold text-red-900">Avoid</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.bad}</p>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-900">Use</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.good}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="negative" className="space-y-4 mt-4">
                <div className="space-y-3">
                  {[
                    { bad: 'Your sleep is bad', good: 'Your sleep could be better' },
                    { bad: 'You failed to track', good: 'Let\'s get back on track' },
                    { bad: 'Unhealthy habits', good: 'Habits we can improve' }
                  ].map((item, i) => (
                    <div key={i} className="grid md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-xs font-semibold text-red-900">Avoid</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.bad}</p>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-900">Use</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.good}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="alarm" className="space-y-4 mt-4">
                <div className="space-y-3">
                  {[
                    { bad: 'Emergency! Seek help now!', good: 'This may need attention. Consider contacting your GP' },
                    { bad: 'Danger! Critical symptoms!', good: 'These symptoms are worth discussing with a healthcare provider' },
                    { bad: 'Serious problem detected!', good: 'It\'s important to get this checked out' }
                  ].map((item, i) => (
                    <div key={i} className="grid md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-xs font-semibold text-red-900">Avoid</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.bad}</p>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-900">Use</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.good}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="commands" className="space-y-4 mt-4">
                <div className="space-y-3">
                  {[
                    { bad: 'You must drink more water', good: 'You might try drinking more water' },
                    { bad: 'You should sleep earlier', good: 'Consider going to bed a bit earlier' },
                    { bad: 'You need to exercise', good: 'It could help to add some movement' }
                  ].map((item, i) => (
                    <div key={i} className="grid md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-xs font-semibold text-red-900">Avoid</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.bad}</p>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-900">Use</span>
                        </div>
                        <p className="text-sm text-gray-800">{item.good}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Interactive Transformer */}
        <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-purple-600" />
              Try It Yourself: Message Transformer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Enter a message to transform:
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Example: You should drink more water. You have poor sleep."
                className="w-full p-3 border-2 border-purple-200 rounded-lg resize-none"
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleTransform} className="bg-purple-600 hover:bg-purple-700">
                Transform with AI Personality
              </Button>
              <Button onClick={() => handleGenerateExample('positive')} variant="outline">
                Generate Example
              </Button>
            </div>

            {transformedMessage && (
              <div className="mt-4 p-4 bg-white border-2 border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-900">Transformed Message:</span>
                </div>
                <p className="text-gray-800">{transformedMessage}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dynamic Tones Examples */}
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle>Dynamic Behavior Examples</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(RESPONSE_EXAMPLES).map(([key, example]) => (
                <div key={key} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-purple-700">
                      <strong>Acknowledge:</strong> {example.acknowledge}
                    </p>
                    <p className="text-blue-700">
                      <strong>Insight:</strong> {example.insight}
                    </p>
                    <p className="text-green-700">
                      <strong>Actions:</strong> {example.actions}
                    </p>
                    <p className="text-pink-700">
                      <strong>Encourage:</strong> {example.encourage}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Usage Guidelines */}
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <CardTitle>For Developers: Usage Guidelines</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <h4>How to Use the AI Personality Module</h4>
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
{`import { 
  generatePersonalizedResponse,
  applyPersonality,
  healthStatusMessages
} from '@/utils/aiPersonality';

// Generate a complete response
const response = generatePersonalizedResponse({
  userState: 'encouraging', // positive, encouraging, concerned, neutral
  insight: 'Your sleep has been inconsistent',
  actions: ['Try going to bed earlier', 'Create bedtime routine'],
  severity: 'low'
});

// Use the response
console.log(response.full); // Complete message
console.log(response.acknowledge); // Just acknowledgment
console.log(response.insight); // Just insight

// Transform existing message
const friendly = applyPersonality('You must drink more water', 'encouraging');

// Use pre-defined messages
const sleepMessage = healthStatusMessages.sleep.good;`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIPersonalityGuide;
